import { useState, useCallback, useEffect } from 'react';
import axios from '../components/AxiosInstance.jsx';
import useSSE from './useSSE';

const useLobby = (gameId) => {
  const [lobby, setLobby] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sseHandlers = {
    player_joined: (data) => {
      setLobby(prev => prev ? {
        ...prev,
        players: [...prev.players.filter(p => p.username !== data.username), data],
      } : prev);
    },
    player_left: (data) => {
      setLobby(prev => prev ? {
        ...prev,
        players: prev.players.filter(p => p.username !== data.username),
      } : prev);
    },
    player_ready: (data) => {
      setLobby(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, is_ready: data.is_ready } : p
        ),
      } : prev);
    },
    country_selected: (data) => {
      setLobby(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username
            ? { ...p, country_short_name: data.country_short_name }
            : p
        ),
      } : prev);
    },
    game_started: (data) => {
      setLobby(prev => prev ? { ...prev, status: 'in_progress' } : prev);
    },
    game_over: (data) => {
      if (data.reason === 'host_left') {
        setLobby(prev => prev ? { ...prev, status: 'finished' } : prev);
        setError('Le host a quitté la partie');
      }
    },
  };

  useSSE(gameId, sseHandlers);

  const fetchLobby = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/multi/lobby/${gameId}`);
      setLobby(res.data.game_session);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchLobby();
  }, [fetchLobby]);

  const selectCountry = useCallback(async (countryShortName) => {
    setError(null);
    try {
      await axios.post('/api/multi/select_country', {
        game_id: parseInt(gameId),
        country_short_name: countryShortName,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  }, [gameId]);

  const toggleReady = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.post('/api/multi/ready', { game_id: parseInt(gameId) });
      return res.data.is_ready;
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  }, [gameId]);

  const startGame = useCallback(async () => {
    setError(null);
    try {
      await axios.post('/api/multi/start', { game_id: parseInt(gameId) });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  }, [gameId]);

  const leaveLobby = useCallback(async () => {
    try {
      await axios.post('/api/multi/leave', { game_id: parseInt(gameId) });
    } catch (err) {
      console.error('Error leaving lobby:', err);
    }
  }, [gameId]);

  return {
    lobby, loading, error,
    fetchLobby, selectCountry, toggleReady, startGame, leaveLobby,
  };
};

export default useLobby;
